import { v4 as uuid } from "uuid";
import { Option } from "../../tailwind/Dropdown";
import { fileWrapper, folderWrapper } from "./NewFilesUploader";

export function createFileWithNewName(
  originalFile: File,
  newName: string,
): File {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
}

export type PermissionSeed = {
  id: string;
  name: string;
  description?: string | null;
};

function toOption(permission: PermissionSeed | null | undefined): Option {
  return {
    value: permission?.id ?? "",
    label: permission?.name ?? "",
    description: permission?.description ?? "",
  };
}

export function isZipFile(file: File): boolean {
  return (
    file.type === "application/zip" ||
    file.type === "application/x-zip-compressed" ||
    file.type === "application/zip-compressed" ||
    file.name.toLowerCase().endsWith(".zip")
  );
}

export function dataTransferHasFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false;
  return Array.from(dataTransfer.types).includes("Files");
}

function traverseFileEntry(
  entry: FileSystemEntry,
  path: string,
): Promise<File[]> {
  return new Promise((resolve, reject) => {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      fileEntry.file((file) => {
        (file as File & { fullPath?: string }).fullPath = path + file.name;
        resolve([file]);
      }, reject);
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const dirReader = dirEntry.createReader();
      const entries: FileSystemEntry[] = [];

      const readEntries = () => {
        dirReader.readEntries(async (results) => {
          try {
            if (results.length === 0) {
              let files: File[] = [];
              for (const item of entries) {
                const nestedFiles = await traverseFileEntry(
                  item,
                  path + dirEntry.name + "/",
                );
                files = files.concat(nestedFiles);
              }
              resolve(files);
            } else {
              entries.push(...Array.from(results));
              readEntries();
            }
          } catch (err) {
            reject(err);
          }
        }, reject);
      };
      readEntries();
    } else {
      resolve([]);
    }
  });
}

export async function getFilesFromDataTransferItem(
  item: DataTransferItem,
): Promise<File[]> {
  const entry = item.webkitGetAsEntry?.();
  if (!entry) {
    return [];
  }
  return traverseFileEntry(entry, "");
}

export function addFileToTree(
  file: File,
  folderTree: folderWrapper[],
  fileItems: fileWrapper[],
  defaultPermission: PermissionSeed | null,
) {
  const relativePath: string =
    file.webkitRelativePath ||
    (file as File & { fullPath?: string }).fullPath ||
    "";
  const fileName = file.name.replace(/\.[^/.]+$/, "");
  const permission = toOption(defaultPermission);

  const parts = relativePath.split("/").filter(Boolean);

  if (parts.length > 1) {
    const folderNames = parts.slice(0, parts.length - 1);
    let currentFolders = folderTree;
    let currentFolder: folderWrapper | undefined;

    folderNames.forEach((folderName) => {
      let folder = currentFolders.find((f) => f.name === folderName);
      if (!folder) {
        folder = {
          id: Math.random(),
          name: folderName,
          uploadProgress: 0,
          status: "pending",
          files: [],
          children: [],
          selectedPermission: permission,
        };
        currentFolders.push(folder);
      }
      currentFolder = folder;
      currentFolders = folder.children!;
    });

    if (fileName.trim() !== "" && currentFolder) {
      currentFolder.files = currentFolder.files || [];
      currentFolder.files.push({
        id: uuid(),
        file,
        name: fileName,
        dataRoomPermissionId: defaultPermission?.id ?? "",
        uploadProgress: 0,
        status: "pending",
        parentFolder: currentFolder,
        selectedPermission: permission,
      });
    }
  } else {
    fileItems.push({
      id: uuid(),
      file,
      name: fileName,
      dataRoomPermissionId: defaultPermission?.id ?? "",
      uploadProgress: 0,
      status: "pending",
      selectedPermission: permission,
    });
  }
}

export async function parseDataTransferItems(
  items: DataTransferItemList | DataTransferItem[],
  defaultPermission: PermissionSeed | null,
): Promise<{ files: fileWrapper[]; folders: folderWrapper[] }> {
  const fileItems: fileWrapper[] = [];
  const folderTree: folderWrapper[] = [];

  const promises = Array.from(items).map(async (item) => {
    const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
    if (!entry) return;

    if (entry.isDirectory) {
      const nestedFiles = await getFilesFromDataTransferItem(item);
      nestedFiles.forEach((f: File) => {
        let file = f;
        if (file.name.endsWith(".qbb.txt")) {
          file = createFileWithNewName(
            file,
            file.name.replace(".qbb.txt", ".qbb"),
          );
        }
        addFileToTree(file, folderTree, fileItems, defaultPermission);
      });
    } else if (entry.isFile) {
      let file = item.getAsFile();
      if (file) {
        if (file.name.endsWith(".qbb.txt")) {
          file = createFileWithNewName(
            file,
            file.name.replace(".qbb.txt", ".qbb"),
          );
        }
        addFileToTree(file, folderTree, fileItems, defaultPermission);
      }
    }
  });

  await Promise.all(promises);
  return { files: fileItems, folders: folderTree };
}

export function payloadContainsZip(
  files: fileWrapper[],
  folders: folderWrapper[],
): boolean {
  const checkFiles = (list: fileWrapper[]) => list.some((f) => isZipFile(f.file));
  if (checkFiles(files)) return true;

  const walk = (folderList: folderWrapper[]): boolean => {
    for (const folder of folderList) {
      if (folder.files && checkFiles(folder.files)) return true;
      if (folder.children && walk(folder.children)) return true;
    }
    return false;
  };

  return walk(folders);
}
